import { createSelector } from '@ngrx/store';
import { parseISO } from 'date-fns';

import { getCompaniesEntities } from '@collections/companies/companies.selectors';
import { TeamMemberServerResponse, TeamMembersListItem } from '@shared/models';
import { adaptJob, Job } from '@shared/models/job';
import { ManagerAutoOptionAdapter } from '@shared/models/manager';
import { MapPluralToSingular } from '@shared/models/team-role.enum';

import { CollectionsState, getCollectionsState } from '../collections.reducer';
import { getManagersEntities } from '../managers/managers.selectors';
import { getUsersEntities, getUsersLoggedIn } from '../users';
import { jobsAdapter, JobsState } from './jobs.reducer';

export const getJobsState = createSelector(
  getCollectionsState,
  (state: CollectionsState) => !!state && state.jobs,
);

export const {
  selectIds: getJobsIds,
  selectEntities: getJobsEntities,
  selectAll: getJobsAll,
  selectTotal: getJobsTotal,
} = jobsAdapter.getSelectors(getJobsState);

export const getJobsLoading = createSelector(
  getJobsState,
  (state: JobsState) => !!state && state.loading,
);

export const getJobsSelectedId = createSelector(
  getJobsState,
  (state: JobsState) => !!state && state.selectedId,
);

export const getJobsSelected = createSelector(
  getJobsEntities,
  getJobsSelectedId,
  (entities, selectedId) => {
    const job = !!selectedId && !!entities && entities[selectedId];
    return !!job ? adaptJob(job) : null;
  },
);

export const getJobsWithCompany = createSelector(
  getJobsSelected,
  getCompaniesEntities,
  (job, companies) =>
    !!job &&
    adaptJob({
      ...job,
      company: !!job.companyId && !!companies && companies[job.companyId],
    }),
);

export const getJob = (id) =>
  createSelector(getJobsEntities, (entities) => !!entities && entities[id]);

export const getJobPageData = createSelector(
  getJobsEntities,
  getCompaniesEntities,
  getManagersEntities,
  (jobs, companies, managers, { id }) =>
    !!id && !!jobs[id]
      ? adaptJob({
          ...jobs[id],
          company: !!jobs[id].companyId && !!companies && companies[jobs[id].companyId],
          companyManager: ManagerAutoOptionAdapter.toOption(managers[jobs[id].companyManagerId]),
        })
      : ({} as Job),
);

/**
 * Match users ids and roles from Job object with User object from collection
 */
export const getTeamMembers = createSelector(
  getUsersEntities,
  getJobsSelected,
  (users, selectedJob) => {
    const {
      regularRecruiters = [],
      hiringManagers = [],
      recruitingManagers = [],
      coordinators = [],
    } = !!selectedJob ? selectedJob : {};

    const teamMembers = {
      regularRecruiters,
      recruitingManagers,
      hiringManagers,
      coordinators,
    };
    /* tslint:disable: align */
    return Object.keys(teamMembers).reduce((previousValue, currentValue) => {
      if (teamMembers[currentValue] && teamMembers[currentValue].length > 0) {
        const membersMapped = [
          ...previousValue,
          ...teamMembers[currentValue].map((teamMember: TeamMemberServerResponse) => {
            if (teamMember.id == null) {
              return;
            }

            return {
              user: users[teamMember.id],
              role: MapPluralToSingular[currentValue],
              lastModifiedDate: parseISO(teamMember.lastModifiedDate),
            };
          }),
        ];

        return membersMapped.filter((el) => el != null);
      }

      return [...previousValue];
    }, []) as TeamMembersListItem[];
  },
);

/**
 * Sort team members list based on lastModifiedDate from the newest one
 */
export const getSortedTeamMembersByLastModifiedDate = createSelector(
  getTeamMembers,
  (teamMembers: TeamMembersListItem[]) => {
    if (teamMembers) {
      return teamMembers.sort((a, b) => +b.lastModifiedDate - +a.lastModifiedDate);
    }
  },
);

export const isLoggedInUserTeamMember = createSelector(
  getUsersLoggedIn,
  getJobsSelected,
  (loggedInUser, selectedJob) => {
    const teamMembers = new Set<number>();
    [
      ...selectedJob.regularRecruiters.map((member) => member.id),
      ...selectedJob.recruitingManagers.map((member) => member.id),
      ...selectedJob.hiringManagers.map((member) => member.id),
    ].forEach((teamMemberId) => teamMembers.add(teamMemberId));
    return teamMembers.has(loggedInUser.id);
  },
);
